import { useCallback } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { useAtom, useAtomValue } from 'jotai'
import { sidebarOpenState } from 'shared/stores/sidebar'
import { SIDEBAR_DESKTOP_MIN_WIDTH } from 'shared/ui/Sidebar/constants'

interface UseSidebarOpenResult {
  /** サイドバーの実効的な開閉状態 */
  sidebarOpen: boolean
  /** 実効値を反転して保存する。以降は画面幅より保存値が優先される */
  toggleSidebar: () => void
}

/**
 * 画面幅由来の既定値。
 * ui/Sidebar が `open` 未指定時に使う判定と同じ閾値を共有する。
 */
function useDefaultSidebarOpen (): boolean {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.up(SIDEBAR_DESKTOP_MIN_WIDTH))
}

/**
 * サイドバーの開閉状態を読むだけの hook。
 * 保存値が未操作(null)の間は画面幅由来の既定値を返す。
 */
export function useSidebarOpenValue (): boolean {
  const storedOpen = useAtomValue(sidebarOpenState)
  const defaultOpen = useDefaultSidebarOpen()

  return storedOpen ?? defaultOpen
}

/**
 * サイドバーの開閉状態と開閉操作を提供する hook。
 *
 * - 未操作(atom が null)の間は画面幅由来の既定値を実効値にするため、
 *   モバイル幅の初回表示ではサイドバーが閉じた状態になる
 * - 一度でも開閉操作をすると boolean が保存され、以降はページ遷移しても保持される
 */
export function useSidebarOpen (): UseSidebarOpenResult {
  const [storedOpen, setStoredOpen] = useAtom(sidebarOpenState)
  const defaultOpen = useDefaultSidebarOpen()
  const sidebarOpen = storedOpen ?? defaultOpen

  const toggleSidebar = useCallback(() => {
    setStoredOpen(!sidebarOpen)
  }, [sidebarOpen, setStoredOpen])

  return { sidebarOpen, toggleSidebar }
}
